// Sparkline cen lotów dla trasy — pokazuje trend z ostatnich ~30 dni.
// Wczytywany asynchronicznie (React Query), żeby nie blokować SSR landingu.

import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowDown, ArrowUp, Minus, TrendingDown } from "lucide-react";
import { getPriceHistory, type PriceHistoryPoint } from "@/lib/price-history.functions";
import { classifyPrice } from "@/lib/price-badge";
import { PriceBadge } from "@/components/price-badge";

function trendOf(points: PriceHistoryPoint[]): "rising" | "falling" | "flat" {
  if (points.length < 2) return "flat";
  const mid = Math.floor(points.length / 2);
  const a = points.slice(0, mid).map((p) => p.displayPrice);
  const b = points.slice(mid).map((p) => p.displayPrice);
  if (!a.length || !b.length) return "flat";
  const avg = (xs: number[]) => xs.reduce((s, x) => s + x, 0) / xs.length;
  const delta = avg(b) - avg(a);
  const ref = avg(a) || 1;
  const pct = delta / ref;
  if (pct > 0.03) return "rising";
  if (pct < -0.03) return "falling";
  return "flat";
}

function fmtPrice(n: number, currency: string) {
  try {
    return new Intl.NumberFormat("pl-PL", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${n} ${currency}`;
  }
}

export function PriceHistorySparkline({
  from,
  to,
  days = 30,
  className,
}: {
  from: string;
  to: string;
  days?: number;
  className?: string;
}) {
  const fetchHistory = useServerFn(getPriceHistory);
  const { data, isLoading } = useQuery({
    queryKey: ["price-history", from, to, days],
    queryFn: () => fetchHistory({ data: { from, to, days } }),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div
        className={`h-16 animate-pulse rounded-lg bg-muted/40 ${className ?? ""}`}
        aria-hidden="true"
      />
    );
  }
  if (!data || data.points.length < 2 || data.min == null || data.max == null) {
    return null;
  }

  const { points, min, max, current, currency } = data;
  const width = 280;
  const height = 56;
  const padY = 4;
  const range = Math.max(1, max - min);
  const stepX = points.length > 1 ? width / (points.length - 1) : 0;
  const coords = points.map((p, i) => {
    const x = i * stepX;
    const y = height - padY - ((p.displayPrice - min) / range) * (height - padY * 2);
    return { x, y, p };
  });
  const linePoints = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const areaPoints = `0,${height} ${linePoints} ${width},${height}`;
  const last = coords[coords.length - 1];
  const trend = trendOf(points);
  const minIdx = points.findIndex((p) => p.displayPrice === min);
  const minCoord = coords[minIdx];

  const TrendIcon = trend === "rising" ? ArrowUp : trend === "falling" ? ArrowDown : Minus;
  const trendColor =
    trend === "rising"
      ? "text-amber-500"
      : trend === "falling"
        ? "text-emerald-500"
        : "text-muted-foreground";
  const trendLabel =
    trend === "rising"
      ? "ceny rosną"
      : trend === "falling"
        ? "ceny spadają"
        : "ceny stabilne";

  const firstDate = new Date(points[0].checkedAt).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "short",
  });
  const lastDate = new Date(points[points.length - 1].checkedAt).toLocaleDateString(
    "pl-PL",
    { day: "numeric", month: "short" },
  );

  return (
    <figure
      className={`rounded-xl border border-border bg-card/60 p-4 ${className ?? ""}`}
      aria-label={`Historia cen ${from} → ${to} z ostatnich ${days} dni`}
    >
      <figcaption className="mb-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5 font-medium">
          <TrendingDown className="h-3.5 w-3.5" aria-hidden="true" />
          Historia cen ({days} dni)
        </span>
        <span className="flex items-center gap-2">
          {current != null && (() => {
            // Avg liczone w trybie "spójnym ze stats" — średnia z dziennych minimum.
            const avg = Math.round(
              points.reduce((s, p) => s + p.displayPrice, 0) / points.length,
            );
            const badge = classifyPrice(current, {
              min,
              avg,
              count: points.length,
              daysSpan: days,
            });
            return <PriceBadge badge={badge} size="xs" />;
          })()}
          <span className={`flex items-center gap-1 font-medium ${trendColor}`}>
            <TrendIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {trendLabel}
          </span>
        </span>
      </figcaption>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={`Wykres historii cen, min ${fmtPrice(min, currency)}, max ${fmtPrice(max, currency)}`}
        className="overflow-visible"
      >
        <defs>
          <linearGradient id={`sparkfill-${from}-${to}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon
          points={areaPoints}
          fill={`url(#sparkfill-${from}-${to})`}
          className="text-primary"
        />
        <polyline
          points={linePoints}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-primary"
        />
        {minCoord && (
          <>
            <circle
              cx={minCoord.x}
              cy={minCoord.y}
              r={3}
              className="fill-emerald-500"
            />
            <title>
              Najniższa cena: {fmtPrice(min, currency)} ({new Date(minCoord.p.checkedAt).toLocaleDateString("pl-PL")})
            </title>
          </>
        )}
        {last && (
          <circle
            cx={last.x}
            cy={last.y}
            r={3}
            className="fill-primary stroke-background"
            strokeWidth={1.5}
          />
        )}
      </svg>

      <div className="mt-2 flex flex-wrap items-end justify-between gap-x-4 gap-y-1 text-xs">
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground">
            Min: <span className="font-semibold text-emerald-600 dark:text-emerald-400">{fmtPrice(min, currency)}</span>
          </span>
          <span className="text-muted-foreground">
            Max: <span className="font-semibold text-foreground">{fmtPrice(max, currency)}</span>
          </span>
          {current != null && (
            <span className="text-muted-foreground">
              Teraz: <span className="font-semibold text-foreground">{fmtPrice(current, currency)}</span>
            </span>
          )}
        </div>
        <span className="text-[10px] text-muted-foreground">
          {firstDate} – {lastDate}
        </span>
      </div>
    </figure>
  );
}
